import { useState, useEffect } from 'react';
import { Send, MapPin, Mail, Phone, CheckCircle, Instagram, Facebook, Linkedin, MessageCircle, Clock, Shield, GraduationCap, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institution: '',
    career: '',
    student_count: '',
    destination: '',
    travel_dates: '',
    itinerary: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data.slice(0, 4));
      } catch (error) {
        setFaqs([
          { id: '1', question: '¿Cuál es el mínimo de estudiantes?', answer: 'El grupo mínimo recomendado es de 15 estudiantes para garantizar las mejores tarifas.' },
          { id: '2', question: '¿Qué incluyen los paquetes?', answer: 'Transporte, hospedaje, seguro, acceso a eventos, guías especializados y coordinación completa.' },
          { id: '3', question: '¿Con cuánta anticipación reservar?', answer: '3 meses para nacionales, 6 meses para internacionales.' },
          { id: '4', question: '¿Los docentes viajan gratis?', answer: 'Sí, viaje y viáticos incluidos para profesores acompañantes.' },
        ]);
      }
    };
    fetchFaqs();
  }, []);

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/collegetraveler', handle: '@collegetraveler', color: 'bg-gradient-to-br from-purple-600 to-pink-500' },
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/collegetraveler', handle: 'College Traveler', color: 'bg-blue-600' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/collegetraveler', handle: 'College Traveler', color: 'bg-blue-700' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        institution: formData.institution,
        student_count: parseInt(formData.student_count) || 0,
        destination: formData.destination,
        travel_dates: formData.travel_dates,
        message: `Carrera: ${formData.career}\n\nItinerario tentativo:\n${formData.itinerary}\n\nMensaje:\n${formData.message}`,
      });
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', institution: '', career: '', student_count: '', destination: '', travel_dates: '', itinerary: '', message: '' });
    } catch (err) {
      setError('Hubo un error al enviar. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?auto=format&fit=crop&w=1920&q=80"
            alt="Contact"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="inline-block text-[#009DE0] font-semibold text-sm uppercase tracking-wider mb-4">
            Contacto
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight">
            Comienza tu
            <br />
            <span className="text-[#009DE0]">aventura</span>
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  ¿Listo para tu próxima aventura académica?
                </h2>
                <p className="text-white/60">
                  Cuéntanos sobre tu proyecto y recibe una cotización personalizada en menos de 24 horas.
                </p>
              </div>

              {/* Quick Benefits */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: Clock, label: '24hrs' },
                  { icon: Shield, label: 'Seguro' },
                  { icon: GraduationCap, label: 'Gratis' },
                ].map((item, index) => (
                  <div key={index} className="bg-white/5 rounded-2xl p-4 text-center border border-white/10">
                    <item.icon size={24} className="text-[#009DE0] mx-auto mb-2" />
                    <span className="text-white text-xs font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="w-12 h-12 bg-[#009DE0]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#009DE0]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Email</p>
                    <p className="text-white font-medium">contacto@collegetraveler.com</p>
                    <p className="text-white font-medium">logistica@collegetraveler.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="w-12 h-12 bg-[#009DE0]/20 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#009DE0]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Teléfono</p>
                    <p className="text-white font-medium">+52 (228) 272 8556</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <div className="w-12 h-12 bg-[#009DE0]/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#009DE0]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Ubicación</p>
                    <p className="text-white font-medium">Veracruz, México</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Button */}
              <a
                href="https://wa.me/522282728556"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="flex items-center justify-center gap-3 bg-green-600 text-white w-full py-4 rounded-full font-bold text-lg hover:bg-green-700 transition-all"
              >
                <MessageCircle size={24} />
                Escríbenos por WhatsApp
              </a>

              {/* Social Links */}
              <div>
                <p className="text-white/50 text-sm uppercase tracking-wider mb-4">Síguenos</p>
                <div className="flex gap-3">
                  {socialLinks.map((social) => {
                    const IconComponent = social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`social-link-${social.name.toLowerCase()}`}
                        className={`w-12 h-12 ${social.color} rounded-xl flex items-center justify-center hover:scale-110 transition-transform`}
                      >
                        <IconComponent size={22} className="text-white" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl p-8 lg:p-10">
                {success ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-[#1A1A1A] mb-2">¡Solicitud enviada!</h3>
                    <p className="text-gray-600 mb-6">Recibirás tu cotización en menos de 24 horas.</p>
                    <button onClick={() => setSuccess(false)} className="text-[#009DE0] font-semibold hover:underline">
                      Enviar otra solicitud
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6">Solicitar Cotización</h2>
                    
                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#1A1A1A] font-medium">Nombre completo *</Label>
                        <Input id="name" name="name" data-testid="input-name" value={formData.name} onChange={handleChange} required placeholder="Tu nombre" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1A1A1A] font-medium">Email *</Label>
                        <Input id="email" name="email" type="email" data-testid="input-email" value={formData.email} onChange={handleChange} required placeholder="tu@email.com" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#1A1A1A] font-medium">Teléfono / WhatsApp</Label>
                        <Input id="phone" name="phone" data-testid="input-phone" value={formData.phone} onChange={handleChange} placeholder="+52 (228) 123 4567" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="institution" className="text-[#1A1A1A] font-medium">Institución *</Label>
                        <Input id="institution" name="institution" data-testid="input-institution" value={formData.institution} onChange={handleChange} required placeholder="Universidad o escuela" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="career" className="text-[#1A1A1A] font-medium">Carrera *</Label>
                        <Input id="career" name="career" data-testid="input-career" value={formData.career} onChange={handleChange} required placeholder="Ej: Ingeniería, Medicina..." className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="student_count" className="text-[#1A1A1A] font-medium">Estudiantes *</Label>
                        <Input id="student_count" name="student_count" type="number" data-testid="input-student-count" value={formData.student_count} onChange={handleChange} required min="1" placeholder="15" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="destination" className="text-[#1A1A1A] font-medium">Destino deseado *</Label>
                        <Input id="destination" name="destination" data-testid="input-destination" value={formData.destination} onChange={handleChange} required placeholder="Ej: Nueva York, CDMX..." className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="travel_dates" className="text-[#1A1A1A] font-medium">Fechas tentativas</Label>
                        <Input id="travel_dates" name="travel_dates" data-testid="input-dates" value={formData.travel_dates} onChange={handleChange} placeholder="Ej: Marzo 2025" className="rounded-xl border-gray-200 bg-gray-50 h-12" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="itinerary" className="text-[#1A1A1A] font-medium">Itinerario tentativo (opcional)</Label>
                        <Textarea id="itinerary" name="itinerary" data-testid="input-itinerary" value={formData.itinerary} onChange={handleChange} placeholder="Describe actividades, lugares, empresas, museos que te gustaría visitar..." rows={3} className="rounded-xl border-gray-200 bg-gray-50 resize-none" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="message" className="text-[#1A1A1A] font-medium">Mensaje adicional</Label>
                        <Textarea id="message" name="message" data-testid="input-message" value={formData.message} onChange={handleChange} placeholder="¿Algo más que debamos saber?" rows={2} className="rounded-xl border-gray-200 bg-gray-50 resize-none" />
                      </div>
                    </div>

                    {error && <p data-testid="form-error" className="text-red-500 text-sm mt-4">{error}</p>}

                    <button type="submit" data-testid="submit-contact" disabled={loading} className="w-full mt-6 bg-[#009DE0] text-white py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all flex items-center justify-center gap-2 disabled:opacity-70">
                      {loading ? (
                        <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Enviando...</>
                      ) : (
                        <><Send size={20} /> Solicitar Cotización</>
                      )}
                    </button>
                    <p className="text-center text-gray-500 text-sm mt-4">Respuesta garantizada en menos de 24 horas</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-4xl font-bold text-white mt-4">Preguntas Frecuentes</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={faq.id} className="bg-white/5 rounded-2xl border border-white/10 px-6">
                <AccordionTrigger className="text-left text-white font-semibold hover:text-[#009DE0] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
